import { useState } from 'react';
import axios from 'axios';
import { UserPlus, X, Check, Eye, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function nextSession(studentId, sessions) {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = sessions
    .filter(s => s.studentIds.includes(studentId) && s.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (upcoming.length === 0) return null;
  try {
    return format(parseISO(upcoming[0].date), "EEE dd/MM", { locale: ptBR });
  } catch { return upcoming[0].date; }
}

export default function AgendaSidebar({
  agendaStudents = [], sessions = [], attendanceStats = {},
  onStudentClick, onAddStudent, token, backendUrl,
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await axios.post(`${backendUrl}/api/admin/agenda/students`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm({ name: '', phone: '' });
      setShowForm(false);
      onAddStudent?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (e, student) => {
    e.stopPropagation();
    setLoadingId(student.id);
    try {
      await axios.patch(
        `${backendUrl}/api/admin/agenda/students/${student.id}`,
        { active: !student.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onAddStudent?.();
    } catch {}
    finally { setLoadingId(null); }
  };

  const handleDelete = async (id) => {
    setLoadingId(id);
    try {
      await axios.delete(
        `${backendUrl}/api/admin/agenda/students/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfirmDeleteId(null);
      onAddStudent?.();
    } catch {}
    finally { setLoadingId(null); }
  };

  const activeStudents = agendaStudents.filter(s => s.active);
  const inactiveStudents = agendaStudents.filter(s => !s.active);

  const StudentCard = ({ student, isInactive = false }) => {
    const next = nextSession(student.id, sessions);
    const stat = attendanceStats[student.id];
    const isLoading = loadingId === student.id;
    const isConfirming = confirmDeleteId === student.id;

    if (isConfirming) {
      return (
        <div className="border-b border-white/5 px-4 py-3 bg-red-950/20">
          <p className="text-xs text-[#A3A3A3] mb-2.5">
            Excluir <span className="text-white font-medium">{student.name}</span>?
            <br />
            <span className="text-[#555]">As presenças registradas serão mantidas.</span>
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handleDelete(student.id)}
              disabled={isLoading}
              className="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Excluindo...' : 'Excluir'}
            </button>
            <button
              onClick={() => setConfirmDeleteId(null)}
              className="px-3 py-1.5 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="border-b border-white/5">
        <div className="flex items-start gap-2 px-3 py-3">
          {/* Info — clicável */}
          <button
            onClick={() => !isInactive && onStudentClick?.(student)}
            className={`flex-1 text-left min-w-0 ${isInactive ? 'opacity-40 cursor-default' : ''}`}
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-sm font-medium leading-tight truncate">{student.name}</span>
              {student.appStudentId && (
                <span className="text-[9px] bg-white/10 text-[#A3A3A3] px-1.5 py-0.5 flex-shrink-0">APP</span>
              )}
            </div>
            {next && !isInactive && (
              <p className="text-[10px] text-[#555]">Próx: <span className="text-[#A3A3A3]">{next}</span></p>
            )}
            {stat && stat.total > 0 && !isInactive && (
              <div className="mt-1.5">
                <div className="flex justify-between items-center mb-0.5">
                  <span className="text-[9px] text-[#555]">frequência</span>
                  <span className={`text-[10px] font-medium ${
                    stat.pct >= 75 ? 'text-green-400' : stat.pct >= 50 ? 'text-yellow-400' : 'text-red-400'
                  }`}>{stat.pct}%</span>
                </div>
                <div className="w-full bg-white/5 h-1">
                  <div className={`h-1 ${
                    stat.pct >= 75 ? 'bg-green-500' : stat.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`} style={{ width: `${stat.pct}%` }} />
                </div>
              </div>
            )}
          </button>

          {/* Ações — apenas para inativos */}
          {isInactive && (
            <div className="flex gap-0.5 flex-shrink-0 mt-0.5">
              <button
                onClick={(e) => handleToggleActive(e, student)}
                disabled={isLoading}
                title="Reativar"
                className="p-1.5 text-[#555] hover:text-[#A3A3A3] transition-colors disabled:opacity-40"
              >
                <Eye size={13} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(student.id); }}
                disabled={isLoading}
                title="Excluir"
                className="p-1.5 text-[#555] hover:text-red-400 transition-colors disabled:opacity-40"
              >
                <Trash2 size={13} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#0d0d0d] border border-white/5">
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3]">Alunos</span>
          <button
            onClick={() => setShowForm(!showForm)}
            className="text-[#A3A3A3] hover:text-white transition-colors"
            title="Novo aluno"
          >
            {showForm ? <X size={14} /> : <UserPlus size={14} />}
          </button>
        </div>
        <p className="text-[10px] text-[#555]">{activeStudents.length} ativo{activeStudents.length !== 1 ? 's' : ''}</p>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="p-4 border-b border-white/5 flex flex-col gap-2">
          <input
            type="text" required placeholder="Nome" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-xs"
          />
          <input
            type="text" placeholder="Telefone (opcional)" value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-xs"
          />
          {error && <p className="text-red-400 text-[10px]">{error}</p>}
          <button type="submit" disabled={saving}
            className="flex items-center justify-center gap-1 py-1.5 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
          >
            <Check size={12} /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </form>
      )}

      <div className="overflow-y-auto max-h-[520px]">
        {activeStudents.length === 0 && (
          <p className="p-4 text-[#555] text-xs text-center">Nenhum aluno ainda.</p>
        )}
        {activeStudents.map(student => (
          <StudentCard key={student.id} student={student} />
        ))}

        {inactiveStudents.length > 0 && (
          <div className="border-t border-white/5">
            <button
              onClick={() => setShowInactive(v => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-[10px] text-[#555] hover:text-[#A3A3A3] uppercase tracking-widest transition-colors"
            >
              <span>Inativos ({inactiveStudents.length})</span>
              {showInactive ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </button>
            {showInactive && inactiveStudents.map(student => (
              <StudentCard key={student.id} student={student} isInactive />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
